import React, { useCallback, useEffect, useState, useRef } from 'react';
import { connect, Provider } from 'react-redux';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import PopupHeader from '../../../components/Popup/PopupHeader';
import PopupBody from '../../../components/Popup/PopupBody';
import PopupButton from '../../../components/Popup/PopupButton';
import * as actions from '../../../../redux/user/actions.user';
import * as analyticsActions from '../../../../redux/analytics/actions.analytics';
import PlanTile from '../PlanTile';
import errorCodes from '../../../../const/errorCodes';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import device from '../../../../services/mini/device';
import { confirmAlert } from '../../../../services/mini/alanConfirmAlert';
import AddFundsPopup from '../../billing/BillingPopups/AddFundsPopup';
import store from '../../../../store';
import { useIsMounted } from '../../../../hooks/useIsMounted';
import errorMessages from '../../../../const/errorMessages';
import withClickOutsideOnOverlay from '../../../../hoc/withClickOutsideOnOverlay';
import { pages, actions as planActions } from '../../../../const/plansAnalytics';
import { reachMetrikaGoal } from '../../../../services/MetrikaService';
import ToasterService from '../../../../services/ToasterService';
import capitalize from '../../../../services/mini/capitalize';
import { checkForGitHubPromoPlan } from '../../../../services/mini/checkForGitHubPromoPlan';
import { getNeededStartsFromGitHubPlan } from '../../../../services/mini/getNeededStartsFromGitHubPlan';
import { canSwitchToGitHubPromoPlan } from '../../../../services/mini/canSwitchToGitHubPromoPlan';
const $ = require('jquery');

function ChangePlanPopup({
    changePlan,
    onBillingPopupClosed,
    currentPlan,
    nextPlan,
    getGithubPromoInfo,
    resetNextPlanTransition,
    trackPlanAction,
    onClose
}) {

    const isMounted = useIsMounted();
    const [isPlanChanging, setIsPlanChanging] = useState(false);
    const [isPlanChanged, setIsPlanChanged] = useState(false);
    const [successChangingPlanMsg, setSuccessChangingPlanMsg] = useState(false);
    const [insufficientFundAmount, setInsufficientFundAmount] = useState(nextPlan.price);
    const [errorMsg, setErrorMsg] = useState(null);
    const [refundAmount, setRefundAmount] = useState(null);
    const [balanceAfterChange, setBalanceAfterChange] = useState(null);
    const popupContentRef = useRef(0);
    useEffect(() => {
        $(popupContentRef.current)
            .animate({
                scrollTop: - 10000 //wierd scroll issue with flex -revers layout
            }, 600);

        changePlan({ planId: nextPlan.id, onlyCheck: true }).then(({
            refundAmount,
            balanceAfterChange
        } = {}) => {
            if (!isMounted.current) return;
            setErrorMsg(null);
            setBalanceAfterChange(+balanceAfterChange);
            setRefundAmount(+refundAmount);
        },
            (errData) => {
                if (!isMounted.current) return;
                if (errData.error === errorCodes.notEnoughMoney) {
                    setInsufficientFundAmount(+errData.insufficientFundAmount / 100);
                    setErrorMsg(errorCodes.notEnoughMoney);
                } else if (errData.error === errorCodes.cardVerificationRequired) {
                    setInsufficientFundAmount(+errData.insufficientFundAmount / 100);
                    setErrorMsg(errorCodes.cardVerificationRequired);
                } else {
                    setInsufficientFundAmount(1);
                    if (errData.error === errorCodes.notPaidUser) {
                        setErrorMsg(errorCodes.notPaidUser);
                    } else {
                        setErrorMsg(errorMessages[errData.error] || errData.error);
                    }
                }
            });
    }, [nextPlan.id, changePlan, isMounted]);

    const addFundsCb = useCallback((event, error) => {
        onClose();
        trackPlanAction({ page: pages.plans, action: planActions.addFundsInPlan });
        confirmAlert({
            customUI: ({ onClose: closeBillingPopup }) => {
                return (
                    <AddFundsPopup
                        onClose={closeBillingPopup}
                        nextPlan={nextPlan}
                        onBillingFinishedCb={(paymentId, paymentType) => {
                            reachMetrikaGoal('onPlanChange');
                            trackPlanAction({ page: pages.plans, action: planActions.changePlan, newPlanId: nextPlan.id, newPlanName: nextPlan.name });
                            changePlan({
                                planId: nextPlan.id,
                                paymentId,
                                paymentType
                            }).then(() => {
                                closeBillingPopup();
                                ToasterService.showInfo({
                                    title: `<b>${capitalize(nextPlan.name)}</b> Plan was applied successfully`
                                });
                            }, (error) => {
                                setIsPlanChanging(false);
                                closeBillingPopup();
                                if (error === errorCodes.notEnoughMoney || error === errorCodes.notPaidUser) {
                                    addFundsCb(null, error);
                                }
                            });
                        }}
                        amount={insufficientFundAmount}
                        store={store}
                        error={error}
                    />
                );
            }
        });
    }, [nextPlan, changePlan, insufficientFundAmount, onClose, trackPlanAction]);

    const onCloseCb = useCallback(() => {
        reachMetrikaGoal('closeChangePlanModal');
        onClose();
    }, [onClose]);

    const stayOnThisPlanCb = useCallback(() => {
        if (nextPlan.isEnterprise) {
            resetNextPlanTransition();
        }
        onCloseCb();
    }, [onCloseCb, resetNextPlanTransition, nextPlan.isEnterprise]);

    const githubStars = getNeededStartsFromGitHubPlan(nextPlan);
    const isGithubPromoPlan = checkForGitHubPromoPlan(nextPlan);

    const changePlanCb = useCallback(async () => {
        if (isGithubPromoPlan) {
            const canSwitch = await canSwitchToGitHubPromoPlan(githubStars, getGithubPromoInfo);
            if (!canSwitch) {
                return;
            }
        }

        setIsPlanChanging(true);
        reachMetrikaGoal('onPlanChange');
        trackPlanAction({ page: pages.plans, action: planActions.changePlan, newPlanId: nextPlan.id, newPlanName: nextPlan.name });

        changePlan({
            planId: nextPlan.id
        }).then(({ message }) => {
            setIsPlanChanging(false);
            setIsPlanChanged(true);
            setSuccessChangingPlanMsg(message);
            onCloseCb();
        }, (error) => {
            setIsPlanChanging(false);
            if (error === errorCodes.notEnoughMoney || error === errorCodes.notPaidUser) {
                addFundsCb(null, error);
            }
        });
    }, [
        changePlan,
        setIsPlanChanging,
        addFundsCb,
        nextPlan.id,
        nextPlan.name,
        trackPlanAction,
        onCloseCb,
        getGithubPromoInfo,
        githubStars,
        isGithubPromoPlan
    ]);


    let okBtn;

    const planWasChanged = isPlanChanged || currentPlan.id === nextPlan.id;
    if ((errorMsg && currentPlan.id !== nextPlan.id) && !nextPlan.isExternalPayment) {
        okBtn = <PopupButton
            label={'Switch to this plan'}
            className={isPlanChanging && 'btn-loader'}
            action={() => {
                addFundsCb(null, errorMsg);
            }} />;
    } else {
        okBtn = <PopupButton
            label="Switch to this plan"
            className={isPlanChanging && 'btn-loader'}
            action={changePlanCb} />;
        if (planWasChanged) {
            okBtn = <PopupButton
                label="OK"
                action={onClose} />;
        }
    }

    let title = '';
    if (planWasChanged) {
        title = 'You have successfully changed your plan!';
    } else {
        if (nextPlan.isEnterprise) {
            title = 'You have a verified Enterprise Plan to switch to';
        } else {
            title = 'You are about to change your Plan';
        }
    }

    return (
        <Provider store={store}>
            <PopupWrapper className={`change-plan-popup ${device.isMobile() && 'mobile'}`}>
                <PopupHeader
                    onCancel={onCloseCb}
                    title={title} />
                <PopupBody className="change-plan-popup_body" noPadding={true} noOverflow={true}>
                    <div className="change-plan-popup_content" ref={popupContentRef}>
                        <div className={`change-plan-popup_plans-from-to`}>
                            {!planWasChanged && <div className="change-plan-popup_from-plan">
                                <div className="change-plan-popup_plan-holder-header from">
                                    Current Plan
                                </div>
                                <PlanTile plan={currentPlan} hidePlanBtn={true} />
                                <div className="action-btn light-grey-with-border"
                                    onClick={stayOnThisPlanCb}>
                                    Stay on the current plan
                                </div>
                            </div>}
                            <div className={`change-plan-popup_to-plan ${nextPlan.isEnterprise ? 'enterprise' : 'new'} ${planWasChanged && 'full-mode'}`}>
                                <div className="change-plan-popup_plan-holder-header to">
                                    {nextPlan.isEnterprise ? 'Enterprise Plan' : 'Next Plan'}
                                </div>
                                <PlanTile plan={nextPlan} hidePlanBtn={true} />
                                {okBtn}
                            </div>
                        </div>
                    </div>
                </PopupBody>
            </PopupWrapper>
        </Provider>
    );
}

function mapStateToProps({ user }, ownProps) {
    return {
        balance: user.balance,
        paidUntilTs: user.paidUntilTs,
        currentPlan: user.currentPlan,
        cards: user.cards
    };
}

function mapDispatchToProps(dispatch) {
    return {
        getPlans: () => dispatch(actions.getPlans()),
        changePlan: (param) => asyncDispatch(actions.changePlan, dispatch, param),
        resetNextPlanTransition: (param) => asyncDispatch(actions.resetNextPlanTransition, dispatch, param),
        getGithubPromoInfo: (param) => asyncDispatch(actions.getGithubPromoInfo, dispatch, param),
        trackPlanAction: (param) => asyncDispatch(analyticsActions.trackPlanAction, dispatch, param),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withClickOutsideOnOverlay(ChangePlanPopup));
